import { useDispatch, useSelector } from 'react-redux';
import {
	selectConsentProvided,
	setPatientConsentIsLoading,
	setPatientConsentProvided,
	setPatientConsentCreatedAt,
	selectConsentCreatedAt,
} from '../../../store/reducers/patientConsetSlice';
import { useEffectAsync } from '../../../helpers';
import {
	useLazyGetUserOriginalIdQuery,
	useLazyPatientConsentQuery,
} from '../../../graphql/graphql';
import useSocket from '../../../Modules/Base/Hooks/useSocket';
import { Socket } from '../../../Modules/Base/Enums/Socket.enum';
import { getMomentDate } from '../../../Utils/dates.util';
import useFilters from '../../../Modules/Base/Hooks/useFilters';

export default function usePatientConsent(patientId: string) {
	const dispatch = useDispatch();
	const [getPatientConsent] = useLazyPatientConsentQuery();
	const [getUserOriginalId] = useLazyGetUserOriginalIdQuery();
	const { handleDateValueAsMoment, handleBoolean } = useFilters();

	const handleLoadData = async () => {
		dispatch(setPatientConsentIsLoading(true));
		if (patientId) {
			const data = await getPatientConsent({ userId: patientId }).unwrap();
			dispatch(
				setPatientConsentProvided(handleBoolean(data?.user?.consentProvider)),
			);

			const createdAt = handleDateValueAsMoment(
				data?.user?.userConsent?.createdAt,
			);
			if (createdAt && createdAt.isValid()) {
				dispatch(setPatientConsentCreatedAt(createdAt.format()));
			}
		} else {
			dispatch(setPatientConsentProvided(false));
			dispatch(setPatientConsentCreatedAt(undefined));
		}
		dispatch(setPatientConsentIsLoading(false));
	};

	useEffectAsync(async () => {
		await handleLoadData();
	}, []);

	const handleOnSocket = async (data: { userId: string }) => {
		const user = await getUserOriginalId({ id: patientId }).unwrap();
		if (user?.user) {
			const originalId = user.user.originalId;
			if (originalId && data.userId === originalId) {
				await handleLoadData();
			}
		}
	};

	useSocket(Socket.USER_CONSENT_ACCEPTED, (data: any) => handleOnSocket(data));

	return {
		isConsentProvided: useSelector(selectConsentProvided),
		consentRequestSendAt: handleDateValueAsMoment(
			useSelector(selectConsentCreatedAt),
		),
	};
}
